import { ImageUploader } from "@/components/ImageUploader";
import { baseUrl } from "@/lib/http";
import { STAGING_STATUS } from "@/types/api";
import { Alert, Box, Button, Grid, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { stagingsApi } from "./api";
import { stagingsKeys } from "./queryKeys";

interface Form {
  address: string;
  status: number;
  notes: string;
  startDate: Date | null;
  endDate: Date | null;
  image: number | null;
  imageUrl: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactPhone2: string;
}

function toAbsoluteUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.startsWith("/") ? `${baseUrl}${url}` : url;
}

const empty: Form = {
  address: "",
  status: 0,
  notes: "",
  startDate: null,
  endDate: null,
  image: null,
  imageUrl: null,
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  contactPhone2: "",
};

export function StagingEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const stagingId = isNew ? null : Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(empty);
  const [err, setErr] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: stagingsKeys.detail(stagingId),
    queryFn: () => stagingsApi.get(stagingId as number),
    enabled: stagingId !== null,
  });

  useEffect(() => {
    if (!detail.data) return;
    const s = detail.data;
    setForm({
      address: s.address,
      status: s.status,
      notes: s.notes,
      startDate: s.startDate ? new Date(s.startDate) : null,
      endDate: s.endDate ? new Date(s.endDate) : null,
      image: s.image,
      imageUrl: toAbsoluteUrl(s.imageFile?.relativePath),
      contactName: s.ownerContactInfo?.name ?? "",
      contactEmail: s.ownerContactInfo?.email ?? "",
      contactPhone: s.ownerContactInfo?.phone ?? "",
      contactPhone2: s.ownerContactInfo?.phone2 ?? "",
    });
  }, [detail.data]);

  const save = useMutation({
    mutationFn: () => {
      if (!form.address.trim()) {
        return Promise.reject(new Error("Address is required"));
      }
      const body = {
        address: form.address,
        status: form.status,
        notes: form.notes,
        startDate: form.startDate?.toISOString() ?? null,
        endDate: form.endDate?.toISOString() ?? null,
        image: form.image,
        contactName: form.contactName,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        contactPhone2: form.contactPhone2,
      };
      return isNew ? stagingsApi.create(body) : stagingsApi.update(stagingId as number, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stagingsKeys.detail(stagingId) });
      void qc.invalidateQueries({ queryKey: stagingsKeys.lists() });
      void qc.invalidateQueries({ queryKey: stagingsKeys.summaries() });
      navBack();
    },
    onError: (e: Error) => setErr(e.message),
  });

  const navBack = () => {
    const params = new URLSearchParams(location.search);
    void nav(params.get("returnTo") || "/administration/stagings");
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1, minWidth: 160 }}>
            {isNew ? "New Staging" : `Staging — ${form.address || "…"}`}
          </Typography>
          <Button onClick={navBack}>Cancel</Button>
          <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
            Save
          </Button>
        </Box>
        {err && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <ImageUploader
              url={form.imageUrl}
              onUploaded={(file) =>
                setForm({ ...form, image: file.id, imageUrl: toAbsoluteUrl(file.url) })}
              onCleared={() => setForm({ ...form, image: null, imageUrl: null })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              required
              label="Address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              margin="dense"
            />
            <Typography variant="subtitle1" sx={{ mt: 2 }}>
              Owner Contact
            </Typography>
            <TextField
              fullWidth
              label="Name"
              margin="dense"
              value={form.contactName}
              onChange={(e) => setForm({ ...form, contactName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              margin="dense"
              value={form.contactEmail}
              onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
            />
            <TextField
              fullWidth
              label="Phone"
              margin="dense"
              value={form.contactPhone}
              onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
            />
            <TextField
              fullWidth
              label="Phone 2"
              margin="dense"
              value={form.contactPhone2}
              onChange={(e) => setForm({ ...form, contactPhone2: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DatePicker
              label="Staging Start"
              value={form.startDate}
              onChange={(d) => setForm({ ...form, startDate: d })}
              slotProps={{ textField: { fullWidth: true, margin: "dense" } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DatePicker
              label="Destaging Start"
              value={form.endDate}
              onChange={(d) => setForm({ ...form, endDate: d })}
              slotProps={{ textField: { fullWidth: true, margin: "dense" } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              required
              select
              label="Status"
              margin="dense"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}
            >
              {Object.entries(STAGING_STATUS).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value.slice(0, 300) })}
              helperText={`${form.notes.length} / 300`}
              margin="dense"
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
